import ReactGA from 'react-ga4';

// Analytics configuration
const MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined)
  || (typeof window !== 'undefined' ? (window as any).__GA_MEASUREMENT_ID__ : undefined)
  || '';
const isDevelopment = import.meta.env.DEV;

// Custom event interfaces for type safety
export interface CustomEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

export interface PageViewEvent {
  page_title?: string;
  page_location?: string;
  page_path?: string;
  custom_parameters?: Record<string, any>;
}

export interface UserProperties {
  user_id?: string;
  user_type?: 'authenticated' | 'anonymous';
  subscription_status?: 'free' | 'premium' | 'enterprise';
  preferred_language?: string;
}

class AnalyticsService {
  private isInitialized = false;
  private isEnabled = true;

  /**
   * Initialize Google Analytics
   */
  initialize(): void {
    try {
      if (this.isInitialized) {
        console.warn('Analytics already initialized');
        return;
      }

      if (!MEASUREMENT_ID || !MEASUREMENT_ID.startsWith('G-')) {
        this.isEnabled = false;
        return;
      }

      // Initialize ReactGA with configuration
      ReactGA.initialize(MEASUREMENT_ID, {
        testMode: isDevelopment,
        debug: isDevelopment,
        gaOptions: {
          // Respect user privacy
          anonymize_ip: true,
          // Custom configuration
          custom_map: {
            custom_parameter_1: 'user_type',
            custom_parameter_2: 'subscription_status'
          }
        }
      });

      this.isInitialized = true;
      
      if (isDevelopment) {
        console.log('🔍 Analytics initialized in development mode');
      }

      // Track initial page view
      this.trackPageView();
      
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
      this.isEnabled = false;
    }
  }

  /**
   * Track page views automatically
   */
  trackPageView(pageData?: PageViewEvent): void {
    if (!this.isEnabled || !this.isInitialized) return;

    try {
      const defaultPageData = {
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname + window.location.search,
      };

      const eventData = { ...defaultPageData, ...pageData };

      ReactGA.send({
        hitType: 'pageview',
        ...eventData
      });

      if (isDevelopment) {
        console.log('📊 Page view tracked:', eventData);
      }
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  /**
   * Track custom events
   */
  trackEvent(event: CustomEvent): void {
    if (!this.isAnalyticsEnabled()) return;

    try {
      // Use react-ga4 event tracking
      ReactGA.event({
        action: event.action,
        category: event.category,
        label: event.label,
        value: event.value,
        ...event.custom_parameters
      });

      if (isDevelopment) {
        console.log('📊 Event tracked:', event.action, event);
      }
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  }

  /**
   * Set user properties for better analytics
   */
  setUserProperties(properties: UserProperties): void {
    if (!this.isEnabled || !this.isInitialized) return;

    try {
      ReactGA.set(properties);

      if (isDevelopment) {
        console.log('👤 User properties set:', properties);
      }
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  /**
   * Track user authentication
   */
  trackUserAuth(userId: string, userType: 'login' | 'signup' | 'logout'): void {
    this.trackEvent({
      action: userType,
      category: 'Authentication',
      label: userType === 'logout' ? undefined : userId,
      custom_parameters: {
        user_id: userType === 'logout' ? undefined : userId
      }
    });

    if (userType === 'logout') {
      this.setUserProperties({ user_id: undefined, user_type: 'anonymous' });
    } else {
      this.setUserProperties({ user_id: userId, user_type: 'authenticated' });
    }
  }

  /**
   * Track chat interactions
   */
  trackChatInteraction(action: 'message_sent' | 'thread_created' | 'thread_deleted', metadata?: Record<string, any>): void {
    this.trackEvent({
      action,
      category: 'Chat',
      custom_parameters: {
        timestamp: new Date().toISOString(),
        ...metadata
      }
    });
  }

  /**
   * Track search queries
   */
  trackSearch(query: string, resultsCount?: number): void {
    this.trackEvent({
      action: 'search',
      category: 'Search',
      label: query,
      value: resultsCount,
      custom_parameters: {
        search_term: query,
        results_count: resultsCount
      }
    });
  }

  /**
   * Track pricing page interactions
   */
  trackPricingInteraction(action: 'view_pricing' | 'select_plan' | 'upgrade_click', planName?: string): void {
    this.trackEvent({
      action,
      category: 'Pricing',
      label: planName,
      custom_parameters: {
        plan_name: planName,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Track subscription events
   */
  trackSubscription(action: 'subscribe' | 'cancel' | 'renew', planName: string, amount?: number): void {
    this.trackEvent({
      action,
      category: 'Subscription',
      label: planName,
      value: amount,
      custom_parameters: {
        plan_name: planName,
        amount,
        currency: 'INR'
      }
    });
  }

  /**
   * Track errors for debugging
   */
  trackError(error: string, category: string = 'JavaScript Error'): void {
    this.trackEvent({
      action: 'error',
      category,
      label: error,
      custom_parameters: {
        error_message: error,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent
      }
    });
  }

  /**
   * Track GA4 ecommerce purchase event.
   * Handles deduplication via localStorage and consent checks.
   * Retries automatically if gtag is not yet loaded.
   */
  trackPurchase(input: {
    transactionId: string;
    gateway: 'stripe' | 'razorpay';
    currency?: string;
    value?: number;
    planName?: string;
    billingPeriod?: string;
    flowType?: string;
  }): void {
    if (!this.isAnalyticsEnabled()) return;
    if (!input.transactionId) return;

    const sentKey = `sarvalok_ga4_purchase_sent_${input.transactionId}`;
    try {
      if (localStorage.getItem(sentKey) === '1') return;
    } catch {}

    const payload: Record<string, unknown> = {
      transaction_id: input.transactionId,
      payment_type: input.gateway,
    };
    if (typeof input.value === 'number') payload.value = input.value;
    if (typeof input.currency === 'string') payload.currency = input.currency;
    if (typeof input.planName === 'string') payload.plan_name = input.planName;
    if (typeof input.billingPeriod === 'string') payload.billing_period = input.billingPeriod;
    if (typeof input.flowType === 'string') payload.flow_type = input.flowType;
    if (typeof input.planName === 'string') {
      payload.items = [
        {
          item_name: input.planName,
          item_category: typeof input.flowType === 'string' ? input.flowType : 'subscription',
        },
      ];
    }

    const emit = () => {
      const gtag = (window as any)?.gtag;
      if (typeof gtag !== 'function') return false;
      gtag('event', 'purchase', payload);
      try {
        localStorage.setItem(sentKey, '1');
        localStorage.removeItem('sarvalok_last_purchase');
      } catch {}
      if (isDevelopment) {
        console.log('📊 Purchase tracked:', input.transactionId, payload);
      }
      return true;
    };

    if (emit()) return;

    // Retry if gtag not yet loaded (e.g. slow script load after checkout redirect)
    const startedAt = Date.now();
    const retryId = window.setInterval(() => {
      if (emit() || Date.now() - startedAt > 10000) {
        window.clearInterval(retryId);
      }
    }, 500);
  }

  /**
   * Track GA4 ecommerce begin_checkout event.
   */
  trackBeginCheckout(input: {
    gateway: string;
    currency?: string;
    value?: number;
    planName?: string;
    billingPeriod?: string;
    flowType?: string;
  }): void {
    if (!this.isAnalyticsEnabled()) return;

    const gtag = (window as any)?.gtag;
    if (typeof gtag !== 'function') return;

    const payload: Record<string, unknown> = {
      items: [
        {
          item_name: input.planName || 'unknown',
          item_category: input.flowType || 'subscription',
        },
      ],
    };
    if (typeof input.value === 'number' && Number.isFinite(input.value) && input.value > 0) payload.value = input.value;
    if (typeof input.currency === 'string' && input.currency) payload.currency = input.currency;
    payload.payment_type = input.gateway;
    payload.flow_type = input.flowType;
    payload.billing_period = input.billingPeriod;

    gtag('event', 'begin_checkout', payload);

    if (isDevelopment) {
      console.log('📊 Begin checkout tracked:', payload);
    }
  }

  /**
   * Enable/disable analytics (for privacy compliance)
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    
    if (isDevelopment) {
      console.log(`📊 Analytics ${enabled ? 'enabled' : 'disabled'}`);
    }
  }

  /**
   * Check if analytics is enabled and ready
   */
  private isAnalyticsEnabled(): boolean {
    // Check if analytics is enabled, initialized, and user has given consent
    const hasConsent = localStorage.getItem('analytics-consent') === 'accepted';
    return this.isEnabled && this.isInitialized && hasConsent;
  }

  /**
   * Get analytics status
   */
  getStatus(): { initialized: boolean; enabled: boolean; measurementId: string; hasConsent: boolean } {
    const hasConsent = localStorage.getItem('analytics-consent') === 'accepted';
    return {
      initialized: this.isInitialized,
      enabled: this.isEnabled,
      measurementId: MEASUREMENT_ID,
      hasConsent
    };
  }

  /**
   * Public method to check if analytics is ready to track
   */
  isReady(): boolean {
    return this.isAnalyticsEnabled();
  }

  /**
   * Enable analytics tracking (for consent management)
   */
  enableAnalytics(): void {
    this.isEnabled = true;
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        analytics_storage:  'granted',
        ad_storage:         'granted',
        ad_user_data:       'granted',
        ad_personalization: 'granted'
      });
    }
  }

  /**
   * Disable analytics tracking (for consent management)
   */
  disableAnalytics(): void {
    this.isEnabled = false;
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        analytics_storage:  'denied',
        ad_storage:         'denied',
        ad_user_data:       'denied',
        ad_personalization: 'denied'
      });
    }
  }
}

// Create and export a singleton instance
export const analytics = new AnalyticsService();

// Export convenience functions for easier usage
export const initializeAnalytics = () => analytics.initialize();
export const trackPageView = (data?: PageViewEvent) => analytics.trackPageView(data);

// Overloaded trackEvent function to handle both string and object parameters
export function trackEvent(action: string, parameters?: Record<string, any>): void;
export function trackEvent(event: CustomEvent): void;
export function trackEvent(actionOrEvent: string | CustomEvent, parameters?: Record<string, any>): void {
  if (typeof actionOrEvent === 'string') {
    // Handle string-based call
    analytics.trackEvent({
      action: actionOrEvent,
      category: parameters?.category || 'User Interaction',
      label: parameters?.label,
      value: parameters?.value,
      custom_parameters: parameters
    });
  } else {
    // Handle object-based call
    analytics.trackEvent(actionOrEvent);
  }
}

export const setUserProperties = (properties: UserProperties) => analytics.setUserProperties(properties);
export const trackUserAuth = (userId: string, userType: 'login' | 'signup' | 'logout') => analytics.trackUserAuth(userId, userType);
export const trackChatInteraction = (action: 'message_sent' | 'thread_created' | 'thread_deleted', metadata?: Record<string, any>) => analytics.trackChatInteraction(action, metadata);
export const trackSearch = (query: string, resultsCount?: number) => analytics.trackSearch(query, resultsCount);
export const trackPricingInteraction = (action: 'view_pricing' | 'select_plan' | 'upgrade_click', planName?: string) => analytics.trackPricingInteraction(action, planName);
export const trackSubscription = (action: 'subscribe' | 'cancel' | 'renew', planName: string, amount?: number) => analytics.trackSubscription(action, planName, amount);
export const trackError = (error: string, category?: string) => analytics.trackError(error, category);
export const trackPurchase = (input: Parameters<AnalyticsService['trackPurchase']>[0]) => analytics.trackPurchase(input);
export const trackBeginCheckout = (input: Parameters<AnalyticsService['trackBeginCheckout']>[0]) => analytics.trackBeginCheckout(input);

export default analytics;
